import React, { useEffect, useMemo, useState } from 'react';

import { cn } from 'lib/helpers/utility';

import Button from '../Button/Button';
import Divider from '../Divider/Divider';
import DragAndDropList from '../DragAndDropList/DragAndDropList';
import Typography from '../Typography/Typography';

import DisplayButtonWrapper from './components/DisplayButtonWrapper';
import { RenderColumnItem } from './components/RenderColumnItem';

/**
 * @type {import('./components/lib/types').DisplayButtonProps}
 */

const DisplayButton = ({
  columns = [],
  disabledActions = [],
  disabledItems = [],
  onColumnChange = () => {},
  onPinChange = () => {},
  onDragEnd = () => {},
  color = 'neutral',
  btnClassName = '',
  textClassName = '',
  pin = true,
  children
}) => {
  const [currentColumns, setCurrentColumns] = useState(columns);

  const indexByField = useMemo(
    () => new Map(currentColumns.map((col, index) => [col.field, index])),
    [currentColumns]
  );

  const visibleColumns = useMemo(
    () => currentColumns.filter((col) => !col.suppressToolPanel),
    [currentColumns]
  );

  useEffect(() => {
    setCurrentColumns(columns);
  }, [columns]);

  const handleColumnChange = (columns) => {
    setCurrentColumns(columns);
    onColumnChange(columns);
  };

  const dragEndHandler = (reorderedVisible) => {
    const suppressedCols = currentColumns
      .map((col, index) => ({ col, index }))
      .filter(({ col }) => col.suppressToolPanel);

    const merged = [...reorderedVisible];
    suppressedCols.forEach(({ col, index }) => {
      merged.splice(Math.min(index, merged.length), 0, col);
    });

    setCurrentColumns(merged);
    onDragEnd(merged);
  };

  return (
    <DisplayButtonWrapper
      buttonContent={(open) => (
        <Button
          variant="tertiary"
          size="xs"
          text="Display"
          icon="grid-6-mono"
          iconColor={color}
          iconShade={600}
          className={cn(`${open && 'bg-neutral-100'}`, btnClassName)}
          textClassName={textClassName}
        />
      )}
      panelContent={() => (
        <div className="flex flex-col gap-2 px-4 py-2">
          {children}
          {children && <Divider />}
          <div className="flex flex-col gap-2">
            <div className="flex justify-between">
              <Typography variant="p" size="small" weight="semibold">
                Columns
              </Typography>
              {pin && (
                <Typography variant="p" size="small" color="neutral-600">
                  Pin
                </Typography>
              )}
            </div>
            <DragAndDropList
              items={visibleColumns}
              onDragEnd={dragEndHandler}
              disabledItems={disabledItems}
              renderItem={(col) => {
                const isDisabled = disabledItems.includes(col.field);
                const fullIndex = indexByField.get(col.field);

                return (
                  <RenderColumnItem
                    col={col}
                    index={fullIndex}
                    onChange={handleColumnChange}
                    onPinChange={onPinChange}
                    pin={pin}
                    disabled={isDisabled}
                    disabledActions={disabledActions}
                  />
                );
              }}
            />
          </div>
        </div>
      )}
    />
  );
};

export default DisplayButton;
